import {
  useState,
  useEffect,
  memo,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef
} from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import StopIcon from '@mui/icons-material/Stop';
import PropTypes from 'prop-types';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import LegalFooter from '#client/components/LegalFooter';
import VeloAvatar from '#client/components/VeloAvatar';

import * as Styled from './ActivePromptInput.style';
import { CLICKSTREAM_COMMON_PROPS } from '../utils/constants';
import NoSSR from '../utils/noSSR';

/**
 * ActivePromptInput Component
 *
 * A chat input component that allows users to type messages and send them.
 * Features auto-resizing textarea for multi-line messages and message sending on Enter key press.
 * Uses uncontrolled mode for optimal performance - manages its own internal state.
 *
 * @param {object} props - Component props
 * @param {boolean} props.loading - Whether the input is in loading state
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {boolean} props.isAnswering - Whether the system is currently rendering/streaming responses
 * @param {boolean} props.error - Whether there's an error state that should show warning
 * @param {string} props.placeholder - Placeholder text for the input
 * @param {Function} props.onSendMessage - Callback function when message is sent
 * @param {Function} props.onStop - Callback function when stop button is clicked
 * @param {boolean} props.showMoreIndicator - Whether to show the "more content below" indicator
 * @param {boolean} props.visibleFooter - Whether to show the legal footer
 * @param {boolean} props.visibleMoreIndicator - Whether to show the "more content below" indicator
 * @returns {object} The ActivePromptInput component
 */
const ActivePromptInput = memo(
  forwardRef(
    (
      {
        loading = false,
        disabled = false,
        isAnswering = false,
        error = false,
        placeholder = 'Ask Velo anything...',
        onSendMessage,
        onStop,
        showMoreIndicator = false,
        visibleMoreIndicator = true,
        visibleFooter = true
      },
      ref
    ) => {
      // Internal state - always uncontrolled for performance
      const [message, setMessage] = useState('');
      const [processing, setProcessing] = useState(false);

      // Ref for the input field to manage focus
      const inputRef = useRef(null);

      // Ref to track pending focus timeout for cleanup
      const focusTimeoutRef = useRef(null);

      const trackButtonPressed = useClickstreamCallback('buttonPressed');

      // Expose setValue method to parent via ref
      useImperativeHandle(
        ref,
        () => ({
          setValue: (value) => {
            setMessage(value);
            // Focus the input after setting value
            requestAnimationFrame(() => {
              if (inputRef.current && !disabled && !processing) {
                inputRef.current.focus();
              }
            });
          },
          clearValue: () => {
            setMessage('');
            // Focus the input after clearing
            requestAnimationFrame(() => {
              if (inputRef.current && !disabled && !processing) {
                inputRef.current.focus();
              }
            });
          },
          getValue: () => message,
          focus: () => {
            if (inputRef.current && !disabled && !processing) {
              inputRef.current.focus();
            }
          }
        }),
        [message, disabled, processing]
      );

      // Focus management - handles both initial focus and focus after answering completes
      useEffect(() => {
        // Clear any pending focus timeout to prevent stale calls
        if (focusTimeoutRef.current) {
          clearTimeout(focusTimeoutRef.current);
          focusTimeoutRef.current = null;
        }

        if (disabled || processing || loading) {
          return undefined;
        }

        if (isAnswering) {
          // Debounced focus when answering stops to avoid multiple quick re-renders
          focusTimeoutRef.current = setTimeout(() => {
            if (inputRef.current && !disabled && !processing && !loading) {
              inputRef.current.focus();
            }
            focusTimeoutRef.current = null;
          }, 150);
        } else {
          // Immediate focus for initial render using requestAnimationFrame
          requestAnimationFrame(() => {
            if (inputRef.current && !disabled && !processing && !loading) {
              inputRef.current.focus();
            }
          });
        }

        // Cleanup function to clear timeout on unmount or dependency change
        return () => {
          if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current);
            focusTimeoutRef.current = null;
          }
        };
      }, [disabled, loading, processing, isAnswering]);

      /**
       * Calculate Lottie Avatar state based on priority system:
       * 1. warning - highest priority (when error = true OR character limit reached)
       * 2. answering - when system is rendering/streaming responses (takes priority over processing)
       * 3. processing - when actively processing user input
       * 4. awaiting - when user has typed something but not sent yet
       * 5. default - when nothing is happening
       */
      const getVeloAvatarState = useCallback(() => {
        if (error || message.length >= 1000) return 'warning';
        if (isAnswering) return 'answering';
        if (processing) return 'thinking';
        if (message.trim().length > 0) return 'awaiting';
        return 'default';
      }, [error, isAnswering, processing, message]);

      const handleStop = useCallback(() => {
        trackButtonPressed({
          ...CLICKSTREAM_COMMON_PROPS,
          elementName: 'stop_chat',
          elementText: 'Stop chat'
        });

        setProcessing(false);
        if (onStop) {
          onStop();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [onStop]);

      const handleSendMessage = useCallback(() => {
        if (!message.trim() || disabled || loading) return;

        if (onSendMessage) {
          onSendMessage(message.trim());
        }
        // Clear input after sending
        setMessage('');
      }, [message, disabled, loading, onSendMessage]);

      const handleKeyPress = useCallback(
        (event) => {
          // Enter sends message (without Shift), Shift+Enter adds new line
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();

            if (!message.trim() || disabled || loading) return;

            trackButtonPressed({
              ...CLICKSTREAM_COMMON_PROPS,
              elementName: 'send_message',
              elementText: message.trim()
            });
            handleSendMessage();
          }
        },
        [handleSendMessage, disabled, loading, message, trackButtonPressed]
      );

      const handleInputChange = useCallback((event) => {
        const newValue = event.target.value;
        // Enforce character limit of 1000
        if (newValue.length <= 1000) {
          setMessage(newValue);
        }
      }, []);

      // Dynamic placeholder based on processing state
      const getPlaceholder = useCallback(() => {
        if (processing || isAnswering) {
          return 'Thinking...';
        }
        return placeholder;
      }, [processing, placeholder, isAnswering]);

      const avatarState = getVeloAvatarState();
      let color = 'rgba(0, 0, 0, 0.42)';
      let bgcolor = 'rgba(0, 0, 0, 0.10)';
      if (avatarState === 'awaiting') {
        color = 'rgba(255, 255, 255, 0.93)';
        bgcolor = 'rgba(0, 0, 0, 0.92)';
      }
      if (processing || isAnswering) {
        color = 'rgba(255, 255, 255, 0.93)';
        bgcolor = 'rgba(0, 0, 0, 0.10)';
      }

      return (
        <Box alignItems="center" display="flex" flexDirection="column">
          {/* More content indicator */}
          {visibleMoreIndicator && (
            <Styled.MoreIndicator visible={showMoreIndicator}>
              <ArrowDownwardIcon color="grey" fontSize="large" />{' '}
              <Text className="MoreIndicator-text">Scroll down to see more</Text>
            </Styled.MoreIndicator>
          )}
          <Styled.GradientContainer
            active={!processing && message.length > 0}
            processing={processing || isAnswering}
          >
            <Styled.AvatarContainer>
              <NoSSR>
                <VeloAvatar color="#FFFFFF" height={48} state={avatarState} width={48} />
              </NoSSR>
            </Styled.AvatarContainer>

            <Styled.InputContainer>
              <Styled.InputRoot
                disabled={disabled || processing}
                inputRef={inputRef}
                maxRows={4}
                minRows={1}
                multiline
                placeholder={`${getPlaceholder()}`}
                value={message}
                variant="standard"
                onChange={handleInputChange}
                onKeyDown={(event) => handleKeyPress(event, message)}
              />
              {/* Character limit error message */}
              {message.length >= 1000 && (
                <Box display="flex" justifyContent="flex-start" pb={1} pt={1}>
                  <Text color="red" size="xs">
                    You&apos;ve reached the 1,000 character limit.
                  </Text>
                </Box>
              )}
            </Styled.InputContainer>
            <Styled.ButtonContainer>
              <Styled.SendStopButton
                aria-label="Send"
                bgcolor={bgcolor}
                clickstreamProps={{
                  ...CLICKSTREAM_COMMON_PROPS,
                  elementName: 'send_message',
                  elementText: message.trim()
                }}
                disabled={!message.trim() || disabled || loading || processing}
                label="Send"
                onClick={processing || isAnswering ? handleStop : handleSendMessage}
              >
                {processing || isAnswering ? (
                  <StopIcon color={color} onClick={handleStop} />
                ) : (
                  <ArrowUpwardRoundedIcon color={color} />
                )}
              </Styled.SendStopButton>
            </Styled.ButtonContainer>

            <Styled.GradientShadow />
          </Styled.GradientContainer>
          {visibleFooter && <LegalFooter />}
        </Box>
      );
    }
  )
);

ActivePromptInput.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  isAnswering: PropTypes.bool,
  error: PropTypes.bool,
  placeholder: PropTypes.string,
  onSendMessage: PropTypes.func,
  onStop: PropTypes.func,
  showMoreIndicator: PropTypes.bool,
  visibleFooter: PropTypes.bool,
  visibleMoreIndicator: PropTypes.bool
};

ActivePromptInput.displayName = 'ActivePromptInput';

export default ActivePromptInput;
